import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, Download } from 'lucide-react';
import Papa from 'papaparse';
import axios from 'axios';
import config from '../config/config';

const PayrollBulkImport = () => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [step, setStep] = useState('upload'); // upload, preview, importing, results

  // Utility function to get cookie
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    if (match) return decodeURIComponent(match[2]);
    return null;
  };

  const requiredHeaders = [
  'admission_number',
  'amount_paid',
  'payment_type',
  'payment_mode',
];
const optionalHeaders = ['student_names','comments'];

const handleFileUpload = (event) => {
  const selectedFile = event.target.files[0];
  if (!selectedFile) return;

  if (!selectedFile.name.endsWith('.csv')) {
    alert('Please upload a CSV file');
    return;
  }

  setFile(selectedFile);

  Papa.parse(selectedFile, {
    complete: (results) => {
      if (results.errors.length > 0) {
        console.error('CSV parsing errors:', results.errors);
      }

      const data = results.data.filter((row) =>
        Object.values(row).some((value) => value && value.trim() !== '')
      );

      console.log('Parsed data:', data); // Debug: Log parsed data
      setCsvData(data);
      validateCsvData(data);
      setStep('preview');
    },
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_'),
  });
};

  const validateCsvData = (data) => {
    const errors = [];

    if (data.length === 0) {
      errors.push('CSV file is empty');
      setValidationErrors(errors);
      return;
    }

    const headers = Object.keys(data[0]);
    const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));

    if (missingHeaders.length > 0) {
      errors.push(`Missing required columns: ${missingHeaders.join(', ')}`);
    }

    // Validate each row
    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because index starts at 0 and we skip header

      // Check required fields
      requiredHeaders.forEach((header) => {
        if (!row[header] || row[header].trim() === '') {
          errors.push(`Row ${rowNumber}: ${header} is required`);
        }
      });

    /**  // Validate email format (if you decide to add email later)
      if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
        errors.push(`Row ${rowNumber}: Invalid email format`);
      }

      // Validate admission_number is unique in the dataset
      const duplicateId = data.find(
        (otherRow, otherIndex) =>
          otherIndex !== index && otherRow.admission_number === row.admission_number
      );
      if (duplicateId) {
        errors.push(`Row ${rowNumber}: Duplicate admission_number ${row.admission_number}`);
      }**/
    });

    setValidationErrors(errors);
  };

  const handleImport = async () => {
    if (validationErrors.length > 0) {
      alert('Please fix validation errors before importing');
      return;
    }

    setImporting(true);
    setStep('importing');

    const token = localStorage.getItem('token');
    if (!token) {
      setImportResults({
        success: false,
        message: 'No authentication token found. Please log in.',
        errors: [],
      });
      setStep('results');
      setImporting(false);
      return;
    }

    try {
      //console.log('Fetching CSRF cookie...');
      const csrfResponse = await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, {
        withCredentials: true,
      });
      //console.log('CSRF Response:', csrfResponse);

      const xsrfToken = getCookie('XSRF-TOKEN');
      //console.log('XSRF Token:', xsrfToken);
      if (!xsrfToken) {
        throw new Error('CSRF token not found');
      }

      //console.log('Sending bulk import request with data:', csvData);
      const response = await axios.post(
        `${config.API_URL}/api/payments/bulk-import`,
        { students: csvData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
          },
          withCredentials: true,
        }
      );

      //console.log('Import Response:', response.data);
      setImportResults(response.data);
      setStep('results');
    } catch (error) {
      console.error('Import error:', error);
      //console.log('Error Response:', error.response?.data);
      setImportResults({
        success: false,
        message:
          error.response?.data?.message || error.message || 'An error occurred during import',
        errors: error.response?.data?.errors || [],
      });
      setStep('results');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    // Fixed template data to match required headers
    const templateData = [
      {
        Student_Names: 'John Kamau',
        Admission_Number: 'STU001',
        Amount_Paid: '2000',
        Receipt_Number: 'QWERTY123',
        Payment_Type: 'Fee/Transport/Diary/Uniform',
        Payment_Mode: 'Bank',
        Comments : 'Sponsor Name'
      },
    ];

    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payment_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const resetImport = () => {
    setFile(null);
    setCsvData([]);
    setValidationErrors([]);
    setImportResults(null);
    setStep('upload');
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Payments Bulk Import</h2>
        <p className="text-gray-600">Upload a CSV file to import multiple students at once</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {['upload', 'preview', 'importing', 'results'].map((stepName, index) => (
            <div key={stepName} className={`flex items-center ${index < 3 ? 'flex-1' : ''}`}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step === stepName
                    ? 'bg-blue-500 text-white'
                    : ['upload', 'preview', 'importing', 'results'].indexOf(step) > index
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-300'
                }`}
              >
                {index + 1}
              </div>
              <span
                className={`ml-2 text-sm ${
                  step === stepName ? 'text-blue-600 font-medium' : 'text-gray-500'
                }`}
              >
                {stepName.charAt(0).toUpperCase() + stepName.slice(1)}
              </span>
              {index < 3 && <div className="flex-1 h-0.5 bg-gray-300 mx-4"></div>}
            </div>
          ))}
        </div>
      </div>

      {/* Upload Step */}
      {step === 'upload' && (
        <div className="space-y-6">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="space-y-2">
              <p className="text-lg font-medium text-gray-700">Upload CSV File</p>
              <p className="text-sm text-gray-500">Select a CSV file containing student data</p>
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="mt-4 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-800 mb-2">CSV Format Requirements:</h3>
            <div className="text-sm text-blue-700 space-y-1">
              <p>
                <strong>Required columns:</strong> {requiredHeaders.join(', ')}
              </p>
              <p>
                <strong>Optional columns:</strong> {optionalHeaders.join(', ') || 'None'}
              </p>
              <p className="mt-2">
                <button
                  onClick={downloadTemplate}
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Template
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preview Step */}
      {step === 'preview' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-medium">Preview & Validation</h3>
              <span className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded">
                {csvData.length} students
              </span>
            </div>
            <button
              onClick={resetImport}
              className="text-gray-500 hover:text-gray-700"
            >
              Upload Different File
            </button>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <XCircle className="w-5 h-5 text-red-500 mr-2" />
                <h4 className="font-medium text-red-800">Validation Errors</h4>
              </div>
              <ul className="text-sm text-red-700 space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Data Preview */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h4 className="font-medium text-gray-700">Data Preview</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    {Object.keys(csvData[0] || {}).map((header) => (
                      <th key={header} className="px-4 py-2 text-left">
                        {header.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvData.slice(0, csvData.length).map((row, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                      {Object.values(row).map((value, cellIndex) => (
                        <td key={cellIndex} className="px-4 py-2">
                          {value || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {csvData.length > csvData.length && (
              <div className="bg-gray-50 px-4 py-2 text-sm text-gray-600 border-t">
                Showing first {csvData.length} rows of {csvData.length} total
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              onClick={handleImport}
              disabled={validationErrors.length > 0}
              className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
            >
              Import Students
            </button>
          </div>
        </div>
      )}

      {/* Importing Step */}
      {step === 'importing' && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">Importing Students...</h3>
          <p className="text-gray-500">Please wait while we process your data</p>
        </div>
      )}

      {/* Results Step */}
      {step === 'results' && importResults && (
        <div className="space-y-6">
          <div
            className={`p-4 rounded-lg ${
              importResults.success
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <div className="flex items-center mb-2">
              {importResults.success ? (
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500 mr-2" />
              )}
              <h3
                className={`font-medium ${
                  importResults.success ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {importResults.success ? 'Import Successful!' : 'Import Failed'}
              </h3>
            </div>
            <p
              className={`text-sm ${
                importResults.success ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {importResults.message}
            </p>
          </div>

          {importResults.success && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Import Summary:</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• Total students processed: {importResults.total || csvData.length}</p>
                <p>• Successfully imported: {importResults.imported || 0}</p>
                <p>• Skipped (duplicates): {importResults.skipped || 0}</p>
                <p>• Failed: {importResults.failed || 0}</p>
              </div>
            </div>
          )}

          {importResults.errors && importResults.errors.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
                <h4 className="font-medium text-yellow-800">Import Errors</h4>
              </div>
              <ul className="text-sm text-yellow-700 space-y-1 max-h-48 overflow-y-auto">
                {importResults.errors.map((error, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex space-x-4">
            <button
              onClick={resetImport}
              className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 font-medium"
            >
              Import More Students
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollBulkImport;